import { useState, useEffect } from 'react';
import { companiesAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

export default function CompanyManageDialog({ open, onClose, onSave }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) fetchCompanies();
  }, [open]);

  const fetchCompanies = async () => {
    try {
      const res = await companiesAPI.list();
      setCompanies(res.data);
    } catch { /* ignore */ }
  };

  const reset = () => { setName(''); setShowAdd(false); setEditId(null); };

  const handleAdd = async () => {
    if (!name.trim()) { toast.error('Entrez un nom'); return; }
    setLoading(true);
    try {
      await companiesAPI.create({ name: name.trim() });
      toast.success('Société ajoutée');
      reset();
      fetchCompanies();
      onSave?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleUpdate = async () => {
    if (!name.trim()) { toast.error('Entrez un nom'); return; }
    setLoading(true);
    try {
      await companiesAPI.update(editId, { name: name.trim() });
      toast.success('Société mise à jour');
      reset();
      fetchCompanies();
      onSave?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette société ?')) return;
    setLoading(true);
    try {
      await companiesAPI.delete(id);
      toast.success('Société supprimée');
      fetchCompanies();
      onSave?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="company-manage-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" strokeWidth={1.5} />
            Gestion des sociétés
          </DialogTitle>
          <DialogDescription>{companies.length} société(s)</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ScrollArea className="max-h-[250px]">
            <div className="space-y-2">
              {companies.map(c => (
                <div key={c.id} data-testid={`company-item-${c.id}`} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <span className="text-sm font-medium">{c.name}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-company-${c.id}`}
                      onClick={() => { setEditId(c.id); setName(c.name); setShowAdd(false); }}>
                      <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" data-testid={`delete-company-${c.id}`}
                      onClick={() => handleDelete(c.id)}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
              {companies.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Aucune société</p>}
            </div>
          </ScrollArea>

          <Separator />

          {(showAdd || editId) ? (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 animate-fade-in-up">
              <Label className="text-sm font-semibold">{editId ? 'Modifier' : 'Nouvelle société'}</Label>
              <Input data-testid="company-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Nom de la société" />
              <div className="flex gap-2 justify-end">
                <Button data-testid="company-cancel-btn" variant="outline" size="sm" onClick={reset}>
                  <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />Annuler
                </Button>
                <Button data-testid="company-save-btn" size="sm" onClick={editId ? handleUpdate : handleAdd} disabled={loading}>
                  <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{editId ? 'Mettre à jour' : 'Ajouter'}
                </Button>
              </div>
            </div>
          ) : (
            <Button data-testid="add-company-btn" variant="outline" className="w-full" onClick={() => setShowAdd(true)}>
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Ajouter une société
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
